import './App.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';

import DashboardLayout from './components/crm/DashboardLayout';
import EmsDashboardLayout from './components/ems/EmsDashboardLayout';
import UserDashboard from './components/user/UserDashboard';
import UserFormView from './components/user/UserFormView';

// EMS Components
import FormBuilderView from './components/ems/FormBuilderView';
import FormRenderer from './components/ems/FormRenderer';
import FormViewer from './components/ems/FormViewer';
import FormsList from './components/ems/FormsList';
import EditFormBuilder from './components/ems/EditFormBuilder';
import FormSubmissions from './components/ems/FormSubmissions';
import AddCompetitionForm from './components/ems/AddCompetition';
import CompetitionList from './components/ems/CompetitionList';
import EditCompetitionForm from './components/ems/EditCompetitionForm';
import AddEditGroupForm from './components/crm/AddEditGroupForm';
import GroupList from './components/ems/GroupList';
import InvitationTypeList from './components/ems/InvitationTypeList';
import AccreditationCategoryList from './components/ems/AccreditationCategoryList';
import EmsRoleList from './components/ems/EmsRoleList';
import EmsHome from './components/ems/EmsHome';
import ParticipantsList from './components/ems/ParticipantsList';
import AddGroupForm from './components/ems/AddGroupForm';
import SearchTable from './components/ems/Search';
import OrganisationCompetitionSearch from './components/ems/OrganisationCompetitionSearch';
import ParticipatingOrganisationsList from './components/ems/ParticipatingOrganisationsList';
import AddParticipantForm from './components/ems/AddParticipantForm';
import AddPersonForm from './components/ems/AddPersonForm';
import AddTemplateForm from './components/ems/AddTemplate';
import TemplateList from './components/ems/TemplateList';
import EditTemplate from './components/ems/EditTemplate';
import ParticipantEdit from './components/ems/ParticipantEdit';
import GuestEdit from './components/ems/GuestEdit';
import SendEmails from './components/ems/SendEmails';
import EmailHistory from './components/ems/EmailHistory';
import ChangeLog from './components/ems/ChangeLog';
import ImageApproval from './components/ems/ImageApproval';
import PeopleDataCleanup from './components/ems/PeopleDataCleanup';
import PersonList from './components/crm/PersonList';
import UpcomingBirthdays from './components/crm/UpcomingBirthdays';
import UserList from './components/UserList';
import ListRoles from './components/crm/ListRoles';
import AddEditPersonForm from './components/crm/AddEditPersonForm';
import AddEditOrganizationForm from './components/crm/AddEditOrganizationForm';
import OrganisationList from './components/crm/OrganisationList';
import CommitteeList from './components/crm/CommitteeList';
import DeliveryPartnerList from './components/crm/DeliveryPartnerList';
import StatusLosAngeles2028List from './components/crm/StatusLosAngeles2028List';
import AdvancedSearchComponent from './components/crm/AdvancedSearch';
import Downloads from './components/crm/Downloads';
import UserHome from './components/user/UserHome';
import Introduction from './components/Introduction';
import ProgressOverview from './components/user/ProgressOverview';
import DocumentsPage from './components/user/DocumentsPage';
import UserGuestRegistration from './components/user/UserGuestRegistration';
import DocumentSigningComplete from './components/DocumentSigningComplete';

import Login from './components/Login';
import UserLogin from './components/UserLogin';
import Register from './components/Register';
import AddHotelForm from './components/ems/AddHotelForm';
import EditHotelForm from './components/ems/EditHotelForm';
import HotelList from './components/ems/HotelList';
import RoomList from './components/ems/RoomList';
import AddRoom from './components/ems/AddRoom';
import EditRoom from './components/ems/EditRoom';
import AssignRooms from './components/ems/AssignRooms';
import CrmChangeLog from './components/crm/ChangeLogCRM';

import RoomsInformation from './components/ems/RoomsInformation';
import EmsDocumentsDownloads from './components/ems/EmsDocumentsDownloads';
import OnsiteRegistration from './components/ems/OnsiteRegistration';
import OnsiteRegistrationForm from './components/ems/onsite/OnsiteRegistrationForm';

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [role, setRole] = useState(''); // "admin" or "user"
  const [username, setUsername] = useState('');
  const [personId, setPersonId] = useState('');
  const [participantId, setParticipantId] = useState('');
  const [authLoading, setAuthLoading] = useState(true);

  // 1. Load auth state on app start
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedRole = localStorage.getItem('role');
    const storedUsername = localStorage.getItem('username');
    const storedPersonId = localStorage.getItem('personId');

    if (token && storedRole) {
      setIsLoggedIn(true);
      setRole(storedRole);
      setUsername(storedUsername || '');
      setPersonId(storedPersonId || '');
      
      // Only bounce away from the login page that matches current role
      // (admin may open /user-login from Participants → Login)
      if (location.pathname === '/admin-login' && storedRole === 'admin') {
        navigate('/', { replace: true });
      } else if (location.pathname === '/user-login' && storedRole === 'user') {
        navigate('/user/dashboard', { replace: true });
      }
    } else {
      setIsLoggedIn(false);
      setRole('');
    }
    setAuthLoading(false);
  }, [location.pathname, navigate]);

  // 2. Logout handler (Defined before useEffects so they can use it)
  const handleLogout = () => {
    const nextLogin = role === 'admin' ? '/admin-login' : '/user-login';
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('personId');

    setIsLoggedIn(false);
    setRole('');
    setPersonId('');
    navigate(nextLogin);
  };

  // 3. INACTIVITY LOGOUT LOGIC
  useEffect(() => {
    let timeoutId;
    const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 Minutes

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (isLoggedIn) {
        timeoutId = setTimeout(() => {
          handleLogout();
          alert("Session expired due to inactivity. Please login again.");
        }, INACTIVITY_LIMIT);
      }
    };

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    if (isLoggedIn) {
      resetTimer();
      activityEvents.forEach(event => window.addEventListener(event, resetTimer));
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      activityEvents.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [isLoggedIn]);

  // 4. Protected Route Component
  const ProtectedRoute = ({ children }) => {
    if (authLoading) return null; 
    if (!isLoggedIn) return <Navigate to="/admin-login" replace />;
    return children;
  };

  // 5. Login handler
  const handleLogin = ({ token, username, role, personId }) => {
    if (!token || !username || !role) return;

    localStorage.setItem('token', token);
    localStorage.setItem('username', username);
    localStorage.setItem('role', role);
    localStorage.setItem('personId', personId || '');

    setIsLoggedIn(true);
    setUsername(username);
    setRole(role);
    setPersonId(personId || '');

    navigate(role === 'admin' ? '/' : '/user/dashboard');
  };

  return (
    <div className="app">
      <Routes>
        {/* Admin Section */}
        <Route
          path="/"
          element={
            authLoading ? null : !isLoggedIn ? (
              <Navigate to="/admin-login" replace />
            ) : (
              <DashboardLayout username={username} handleLogout={handleLogout} />
            )
          }
        >
          {isLoggedIn && (
            <>
              <Route index element={<Navigate to="/individuals" replace />} />
              <Route path="/individuals" element={<PersonList />} />
              <Route path="/birthdays" element={<UpcomingBirthdays />} />
              <Route path="/list-roles" element={<ListRoles />} />
              <Route path="/user-list" element={<UserList />} />
              <Route path="/organizations" element={<OrganisationList />} />
              <Route path="/committees" element={<CommitteeList />} />
              <Route path="/delivery-partners" element={<DeliveryPartnerList />} />
              <Route path="/status-los-angeles-2028" element={<StatusLosAngeles2028List />} />
              <Route path="/edit-person/:id" element={<AddEditPersonForm />} />
              <Route path="/add-person" element={<AddEditPersonForm />} />
              <Route path="/organisation/:id" element={<AddEditOrganizationForm />} />
              <Route path="/organisation" element={<AddEditOrganizationForm />} />
              <Route path="/crm-changelog" element={<CrmChangeLog/>} />
              <Route path="advanced-search" element={<AdvancedSearchComponent />} />
              <Route path="/downloads" element={<Downloads />} />
            </>
          )}
        </Route>

        {/* Auth Routes */}
        <Route path="/admin-login" element={<Login handleLogin={handleLogin} />} />
        <Route path="/user-login" element={<UserLogin handleLogin={handleLogin} />} />
        <Route path="/register" element={<Register />} />

        {/* EMS Dashboard */}
        <Route
          path="/ems"
          element={
            authLoading ? null : !isLoggedIn ? (
              <Navigate to="/admin-login" replace />
            ) : (
              <EmsDashboardLayout handleLogout={handleLogout} />
            )
          }
        >
          <Route path="home-page" element={<EmsHome />} />
          <Route path="competitions" element={<CompetitionList />} />
          <Route path="participants" element={<ParticipantsList key="all" />} />
          <Route path="participants/athletes" element={<ParticipantsList key="athletes" lockedGroupName="Athlete" />} />
          <Route path="participants/caddies" element={<ParticipantsList key="caddies" lockedGroupName="Caddie" />} />
          <Route path="participating-organisations" element={<ParticipatingOrganisationsList />} />
          <Route path="templates" element={<TemplateList />} />
          <Route path="hotels" element={<HotelList />} />
          <Route path="rooms" element={<RoomList />} />
          <Route path="competitions/add-competition" element={<AddCompetitionForm />} />
          <Route path="competitions/:id" element={<EditCompetitionForm />} />
          <Route path="groups/add-group" element={<AddGroupForm />} />
          <Route path="groups" element={<GroupList />} />
          <Route path="groups/:id/edit" element={<AddEditGroupForm />} />
          <Route path="roles" element={<EmsRoleList />} />
          <Route path="invitation-types" element={<InvitationTypeList />} />
          <Route path="accreditation-categories" element={<AccreditationCategoryList />} />
          <Route path="participants/add-participant" element={<AddParticipantForm />} />
          <Route path="search" element={<SearchTable />} />
          <Route path="organisations/search" element={<OrganisationCompetitionSearch />} />
          <Route path="participants/edit-participant/:id" element={<ParticipantEdit />} />
          <Route path="participants/:participantId/guests/:guestId/edit" element={<GuestEdit />} />
          <Route path="persons/add-person" element={<AddPersonForm />} />
          <Route path="templates/add-template" element={<AddTemplateForm />} />
          <Route path="templates/:id/edit" element={<EditTemplate />} />
          <Route path="send-emails" element={<SendEmails />} />
          <Route path="communication-history" element={<EmailHistory />} />
          <Route path="forms/add" element={<FormBuilderView />} />
          <Route path="forms" element={<FormsList />} />
          <Route path="forms/:id" element={<FormRenderer />} />
          <Route path="forms/edit/:id" element={<EditFormBuilder />} />
          <Route path="submissions/:id" element={<FormSubmissions />} />
          <Route path="forms/view/:id" element={<FormViewer />} />
          <Route path="changelog" element={<ChangeLog />} />
          <Route path="hotels/add-hotel" element={<AddHotelForm />} />
          <Route path="hotels/:id" element={<EditHotelForm />} />
          <Route path="rooms/add-room" element={<AddRoom />} />
          <Route path="rooms/:id" element={<EditRoom />} />
          <Route path='assign-rooms' element ={<AssignRooms/>} />
          <Route path='bookings' element={<RoomsInformation />} />
          <Route path='image-approval' element={<ImageApproval />} />
          <Route path='documents-downloads' element={<EmsDocumentsDownloads />} />
          <Route path="onsite-registration" element={<OnsiteRegistration />} />
          <Route path="data-cleanup" element={<PeopleDataCleanup />} />
        </Route>

        {/* Standalone onsite registration form — no sidebar, auth checked internally */}
        <Route path="/onsite-registration-form" element={<OnsiteRegistrationForm />} />

        {/* User Dashboard — requires JWT (same as EMS/CRM) */}
        <Route
          path="/user"
          element={
            authLoading ? null : !isLoggedIn || role !== 'user' ? (
              <Navigate to="/user-login" replace />
            ) : (
              <UserDashboard username={username} handleLogout={handleLogout} />
            )
          }
        >
          <Route path="dashboard" element={<ProgressOverview />} />
          <Route path="forms/:id" element={<FormRenderer />} />
          <Route path="form/view/:pathSegment/:id" element={<UserFormView />} />
          <Route path="guest-registration" element={<UserGuestRegistration />} />
          <Route path="home" element={<UserHome />} />
          <Route path="introduction" element={<Introduction />} />
          <Route path="esign-documents" element={<DocumentsPage participantId={participantId} />} />
          <Route path="esign-documents/complete" element={<DocumentSigningComplete />} />
        </Route>

        <Route path="*" element={<Navigate to="/admin-login" />} />
      </Routes>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}